import Slider from 'react-slick';

import { PsModalTuner } from 'stencilSetup';
import { ContentBlockDto } from 'enrollment/queries/usePageContentQuery/types';
import { SanitizedPageContent } from './SanitizedContent';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

type PageContentModalProps = {
  title: string;
  body: string;
};

type PageContentBlockProps = {
  contentBlock: ContentBlockDto;
  setModalPopupContent: ({ title, body }: PageContentModalProps) => void;
};

type RotatingContentBlockProps = {
  contentBlocks: ContentBlockDto[];
  setModalPopupContent: ({ title, body }: PageContentModalProps) => void;
};

export function PageContentBlock({
  contentBlock,
  setModalPopupContent,
}: PageContentBlockProps) {
  const blockType = contentBlock.block_type;

  switch (blockType) {
    case 'content':
      return (
        <HtmlBlock
          contentBlock={contentBlock}
          setModalPopupContent={setModalPopupContent}
        />
      );
    case 'documents':
      return <DocumentsBlock contentBlock={contentBlock} />;
    case 'rotating':
      return (
        <RotatingContentBlock
          contentBlocks={contentBlock.rotating_content}
          setModalPopupContent={setModalPopupContent}
        />
      );
    default:
      return null;
  }
}

function HtmlBlock({
  contentBlock,
  setModalPopupContent,
}: PageContentBlockProps) {
  const { content } = contentBlock;

  function handlePopupClick() {
    setModalPopupContent({
      title: contentBlock.label || '',
      body: contentBlock.popup_content || '',
    });
  }

  return (
    <>
      <ContentBlockImage contentBlock={contentBlock} />
      <ContentBlockLabel
        contentBlock={contentBlock}
        onClick={handlePopupClick}
      />
      <SanitizedPageContent html={content} classes="content-block-content" />
    </>
  );
}

function DocumentsBlock({ contentBlock }: { contentBlock: ContentBlockDto }) {
  const { content, documents } = contentBlock;

  return (
    <>
      <ContentBlockImage contentBlock={contentBlock} />
      <ContentBlockLabel contentBlock={contentBlock} />
      <SanitizedPageContent html={content} classes="content-block-content" />

      {documents.map((doc, i) => {
        return (
          <div className="content-block-documents docLink" key={i}>
            <a href={doc.document_url}>
              {doc.label || ''}
              <span className="sr-only">{doc.description}</span>
            </a>
          </div>
        );
      })}
    </>
  );
}

function RotatingContentBlock({
  contentBlocks,
  setModalPopupContent,
}: RotatingContentBlockProps) {
  return (
    <Slider
      autoplay={true}
      autoplaySpeed={3000}
      dots
      infinite
      speed={500}
      slidesToShow={1}
      slidesToScroll={1}
    >
      {contentBlocks
        .filter((content) => content)
        .map((content, i) => {
          return (
            <div key={`rotating-content-${content.name}-${i}`}>
              <HtmlBlock
                contentBlock={content}
                setModalPopupContent={setModalPopupContent}
              />
            </div>
          );
        })}
    </Slider>
  );
}

function ContentBlockImage({
  contentBlock,
}: {
  contentBlock: ContentBlockDto;
}) {
  const { icon_image: iconImage, label } = contentBlock;

  if (!iconImage) {
    return null;
  }

  return <img src={iconImage} alt={label || ''} className="bg-contain" />;
}

function stripHtmlTags(text: string): string {
  if (!text) {
    return '';
  }

  // First create a temporary element to decode HTML entities
  const doc = new DOMParser().parseFromString(text, 'text/html');
  // Get the text content which handles HTML entities
  const decodedText = doc.body.textContent || '';
  // Remove any remaining HTML tags
  return decodedText.replace(/<[^>]*>?/g, '');
}

function ContentBlockLabel({
  contentBlock,
  onClick,
}: {
  contentBlock: ContentBlockDto;
  onClick?: () => void;
}) {
  const { label, url, url_type: urlType } = contentBlock;
  const isPopup = urlType === 'popup_content';
  const isNavigate = urlType === 'navigate';
  const isNavigateNewWindow = urlType === 'new_window';
  const cleanLabel = stripHtmlTags(label || '');

  function onClickWithPreventDefault(e: any) {
    e.preventDefault();
    if (onClick) {
      onClick();
    }
  }

  if (isPopup) {
    return (
      <button
        type="button"
        className="btn content-label text-decoration-none content-block-label"
        data-bs-toggle="modal"
        data-bs-target="#page-content-modal"
        data-testid="content-label"
        onClick={onClickWithPreventDefault}
      >
        {cleanLabel}
      </button>
    );
  } else if (isNavigate || isNavigateNewWindow) {
    return (
      <a
        href={url || ''}
        target={isNavigateNewWindow ? '_blank' : '_self'}
        rel={isNavigateNewWindow ? 'noopener noreferrer' : undefined}
        className="content-label"
        data-testid="content-label"
        onClick={onClickWithPreventDefault}
      >
        {cleanLabel}
      </a>
    );
  }

  return (
    <p className="content-label" data-testid="content-label">
      {cleanLabel}
    </p>
  );
}

export function PageContentModal({ title, body }: PageContentModalProps) {
  return (
    <PsModalTuner
      modalId="page-content-modal"
      modalTitle={stripHtmlTags(title) || ' '} // modal dies without a title
      modalChild={true}
    >
      {/* Modal children must be wrapped in a html tag or this renders behind drawer */}
      <div>
        <SanitizedPageContent html={body} />
      </div>
    </PsModalTuner>
  );
}
